import { useState, useSyncExternalStore, type ReactNode } from 'react'
import { Link, Navigate, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { BriefcaseBusiness, CreditCard, LayoutGrid, LogOut, ScrollText, Users } from 'lucide-react'
import { cx } from '@/lib/clases'
import { useAtajo } from '@/lib/atajos'
import { cerrarSesion, sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import { usePerfilDespacho } from '@/dominio/despacho/useDespacho'
import { Banner } from '@/ui/Banner'
import { BarraSuperior } from './BarraSuperior'
import { PanelAtajos } from './PanelAtajos'
import { filaDeDespacho, filasDelSelector, useCambiarDeSesion } from './selectorDeSesion'

/**
 * El armazón de la **sesión del despacho contable** (`docs/adr/0021`).
 *
 * Es otra aplicación dentro de la misma, como pide la consola del contador en
 * `docs/07`: sin el menú de un contribuyente, porque aquí no hay contribuyente
 * —el testigo no lleva ninguno— sino una cartera. La barra superior es la del
 * portal, con el mismo selector (`Ctrl+K`): desde ella se entra a cualquier
 * cliente sin volver a iniciar sesión.
 *
 * Una ruta que no es del despacho se devuelve a la cartera: sus consultas
 * pedirían datos de un contribuyente con un testigo que no lleva ninguno, y el
 * servidor las negaría todas.
 */

interface Elemento {
  a: string
  etiqueta: string
  Icono: typeof Users
  soloTitular?: boolean
  exacta?: boolean
}

const ELEMENTOS: readonly Elemento[] = [
  { a: '/despacho', etiqueta: 'Cartera', Icono: LayoutGrid, exacta: true },
  { a: '/despacho/clientes', etiqueta: 'Clientes', Icono: BriefcaseBusiness },
  { a: '/despacho/equipo', etiqueta: 'Equipo', Icono: Users },
  { a: '/despacho/cuenta', etiqueta: 'Cuenta con Agentical Group', Icono: CreditCard },
  { a: '/despacho/auditoria', etiqueta: 'Auditoría', Icono: ScrollText, soloTitular: true },
]

function inicialesDe(email: string): string {
  const local = email.split('@')[0] ?? email
  const partes = local.split(/[._-]/).filter(Boolean)
  const letras = partes.length > 1 ? partes[0]![0]! + partes[1]![0]! : local.slice(0, 2)
  return letras.toUpperCase()
}

export function LayoutDespacho({ children }: { children: ReactNode }) {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const ruta = useRouterState({ select: (estado) => estado.location.pathname })
  const cliente = useQueryClient()
  const perfil = usePerfilDespacho()
  const cambio = useCambiarDeSesion()
  const [atajosAbiertos, setAtajosAbiertos] = useState(false)

  useAtajo({ tecla: '?', shift: true }, () => setAtajosAbiertos(true))

  if (sesion === null || sesion.despacho === null) return null

  if (ruta !== '/despacho' && !ruta.startsWith('/despacho/')) {
    return <Navigate to="/despacho" replace />
  }

  const despacho = sesion.despacho
  const activo = filaDeDespacho(despacho)
  const esTitular = despacho.rol === 'titular'

  const salir = (): void => {
    // Lo cacheado es de una cartera entera: no se deja para quien se siente después.
    cliente.clear()
    cerrarSesion()
  }

  return (
    <div className="flex min-h-screen flex-col bg-gris-50">
      <BarraSuperior
        tenants={filasDelSelector(sesion)}
        tenantActivo={activo}
        onCambiarTenant={(id) => {
          if (id !== activo.id && !cambio.cambiando) void cambio.cambiar(id)
        }}
        estadoDGII="normal"
        usuario={{ nombre: sesion.email, iniciales: inicialesDe(sesion.email) }}
      />

      {cambio.error !== null ? (
        <Banner tono="rojo" titulo="No se entró al cliente" onCerrar={cambio.limpiarError}>
          {cambio.error}
        </Banner>
      ) : null}

      {perfil.data && perfil.data.cuenta.etapa !== 'al_dia' ? (
        <Banner
          tono={perfil.data.cuenta.cierraCartera || perfil.data.cuenta.cierraInvitaciones ? 'rojo' : 'ambar'}
          titulo="La cuenta del despacho con Agentical Group tiene un aviso"
        >
          {perfil.data.cuenta.mensaje}
        </Banner>
      ) : null}

      {despacho.estado === 'suspendido' ? (
        <Banner tono="rojo" titulo="El despacho está suspendido en la plataforma">
          Mientras tanto no ve su cartera ni entra a sus clientes. Comuníquese con Agentical Group.
        </Banner>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Navegación del despacho"
          style={{ width: 'var(--ancho-barra-lateral)' }}
          className="flex shrink-0 flex-col border-r border-gris-300 bg-white sin-impresion"
        >
          <div className="flex flex-col gap-1 border-b border-gris-200 px-3 py-3">
            <span className="text-etiqueta font-medium uppercase tracking-wide text-gris-600">Despacho contable</span>
            <span className="truncate text-cuerpo font-medium text-gris-900" title={despacho.nombre}>
              {despacho.nombre}
            </span>
            <span className="text-etiqueta text-gris-600">
              {esTitular ? 'Titular' : 'Auxiliar'}
              {despacho.verificado ? ' · verificado por Agentical Group' : ' · sin verificar'}
            </span>
          </div>

          <ul className="flex-1 py-3">
            {ELEMENTOS.filter((e) => !e.soloTitular || esTitular).map((elemento) => (
              <li key={elemento.a}>
                <EnlaceDespacho elemento={elemento} ruta={ruta} />
              </li>
            ))}
          </ul>

          <div className="border-t border-gris-300 p-2">
            <button
              type="button"
              onClick={salir}
              className="flex h-[var(--alto-control)] w-full items-center gap-3 rounded-md px-2 text-cuerpo text-gris-700 hover:bg-gris-100"
            >
              <LogOut aria-hidden="true" className="size-5" />
              Salir
            </button>
          </div>
        </nav>

        <main className="area-contenido min-w-0 flex-1 p-6">{children}</main>
      </div>

      <PanelAtajos abierto={atajosAbiertos} onCerrar={() => setAtajosAbiertos(false)} />
    </div>
  )
}

function EnlaceDespacho({ elemento, ruta }: { elemento: Elemento; ruta: string }) {
  const { a, etiqueta, Icono, exacta } = elemento
  const activo = exacta ? ruta === a || ruta === `${a}/` : ruta === a || ruta.startsWith(`${a}/`)

  return (
    <Link
      to={a}
      aria-current={activo ? 'page' : undefined}
      className={cx(
        'flex h-[var(--alto-control)] items-center gap-3 border-l-[3px] px-3 text-cuerpo',
        activo ? 'border-marca bg-marca-tenue font-medium text-marca' : 'border-transparent text-gris-700 hover:bg-gris-100',
      )}
    >
      <Icono aria-hidden="true" className="size-5 shrink-0" />
      <span className="flex-1 truncate">{etiqueta}</span>
    </Link>
  )
}
